import React, { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useIntl, defineMessages } from 'react-intl';
import classnames from 'classnames';
import { useFlexLayout, useTable } from 'react-table';

import { ReactTableWrapper } from 'ui-components/components/react-table-wrapper/react-table-wrapper';
import { Text, TextType } from 'ui-components/components/text';

import { useIsShipStationAnalyticsFeatureEnabled } from '../../../../feature-gates/use-is-shipstation-analytics-feature-enabled';
import Global from '../../../../store/global-selectors';
import { doubleDash } from '../../constants';
import { emptyArray } from 'ui-components/default-props/empty';

import style from '../common-styles.module.less';
import analyticsStyle from '../../analytics-style.module.less';

const TITLE = 'title';
const USER = 'user';
const QTY = 'qty';

const NUMBER_OF_USERS_CUTOFF = 4;

const messages = defineMessages({
  [TITLE]: {
    defaultMessage: 'Shipments By User',
    id: '5gjnOb',
    description: 'shipments by user table header',
  },
  [USER]: {
    defaultMessage: 'User name',
    id: 'NN9799',
    description: 'shipments by user table user name header',
  },
  [QTY]: {
    defaultMessage: 'Qty',
    id: 'bQawBU',
    description: 'shipments by user table quantity (abbreviated) header',
  },
});

type ShipmentsByUserRow = {
  user: string;
  qty: string;
};

type ShipmentsByUserData = {
  userId?: number;
  name: string;
  count: number;
};

type ShipmentsByUserTableProps = {
  data?: ShipmentsByUserData[];
};

export const ShipmentsByUserTable = ({ data = emptyArray as ShipmentsByUserData[] }: ShipmentsByUserTableProps) => {
  const intl = useIntl();
  const sellerUsers = useSelector(Global.selectSellerUsers);
  const isShipStationAnalyticsFeatureEnabled = useIsShipStationAnalyticsFeatureEnabled();

  const tableTitle = useMemo(() => intl.formatMessage(messages[TITLE]), [intl]);

  const columns = useMemo(
    () => [
      {
        Header: () => {
          const title = intl.formatMessage(messages[USER]);
          return isShipStationAnalyticsFeatureEnabled ? (
            <div className={classnames(analyticsStyle['analytics-table-header'], analyticsStyle.left)}>{title}</div>
          ) : (
            title
          );
        },
        accessor: USER as typeof USER,
        Cell: ({ value }: { value: string }) =>
          isShipStationAnalyticsFeatureEnabled ? (
            <div className={classnames(analyticsStyle['analytics-table-header'], analyticsStyle.left)}>{value}</div>
          ) : (
            value
          ),
      },
      {
        Header: () => {
          const title = intl.formatMessage(messages[QTY]);
          return (
            <div
              className={classnames(style.right, {
                [analyticsStyle['analytics-table-header']]: isShipStationAnalyticsFeatureEnabled,
              })}
            >
              {title}
            </div>
          );
        },
        accessor: QTY as typeof QTY,
        Cell: ({ value }: { value: string }) => (
          <div
            className={classnames(style.right, {
              [analyticsStyle['analytics-table-header']]: isShipStationAnalyticsFeatureEnabled,
            })}
          >
            {value}
          </div>
        ),
      },
    ],
    [intl, isShipStationAnalyticsFeatureEnabled],
  );

  const tableData = useMemo(() => {
    if (!data.length && sellerUsers.length) {
      return sellerUsers.slice(0, NUMBER_OF_USERS_CUTOFF).map((user: { name: string }) => ({
        user: user.name,
        qty: doubleDash,
      }));
    }
    return data.map((user) => ({
      user: user.name,
      qty: intl.formatNumber(user.count),
    }));
  }, [data, sellerUsers, intl]);

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } = useTable<ShipmentsByUserRow>(
    {
      columns,
      data: tableData,
    },
    useFlexLayout,
  );

  return (
    <>
      <div
        className={classnames(
          { [style['table-title']]: !isShipStationAnalyticsFeatureEnabled },
          { [analyticsStyle['analytics-table-title']]: isShipStationAnalyticsFeatureEnabled },
        )}
      >
        <Text textType={TextType.H3} value={tableTitle} />
      </div>
      <ReactTableWrapper
        className={classnames({
          [analyticsStyle['analytics-table']]: isShipStationAnalyticsFeatureEnabled,
        })}
        getTableBodyProps={getTableBodyProps}
        getTableProps={getTableProps}
        headerGroups={headerGroups}
        prepareRow={prepareRow}
        rows={rows}
      />
    </>
  );
};
